/**
 * 結果画面の「どの瞬間を載せる？」（共有カードに載せる盤面を選ぶ）。
 *
 * 選んだ1枚が、「画像で共有」「画像保存」で作るカードの左に入る。
 * 名場面が1枚しかないとき（最後の盤面だけ）は選ぶものがないので出さない。
 */

import { defaultMoment, momentShortLabel, momentThumbnail, type Moment } from './MomentCapture';

export class MomentPicker {
  readonly element: HTMLElement;
  private selected: Moment | null;

  constructor(
    private readonly moments: readonly Moment[],
    private readonly onSelect: (moment: Moment) => void,
  ) {
    this.selected = defaultMoment(moments);
    this.element = document.createElement('div');
    this.element.className = 'moment-picker';
    if (moments.length < 2) {
      this.element.hidden = true;
      if (this.selected) onSelect(this.selected);
      return;
    }
    const caption = document.createElement('p');
    caption.className = 'moment-picker__caption';
    caption.id = `moment-picker-${Math.random().toString(36).slice(2, 8)}`;
    caption.textContent = '画像の場面';
    const row = document.createElement('div');
    row.className = 'moment-picker__row';
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-labelledby', caption.id);

    const buttons = moments.map((moment) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'moment-picker__item';
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-label', moment.label);
      const thumb = momentThumbnail(moment, 22);
      thumb.className = 'moment-picker__thumb';
      thumb.setAttribute('aria-hidden', 'true');
      const name = document.createElement('span');
      name.className = 'moment-picker__name';
      name.textContent = momentShortLabel(moment);
      btn.append(thumb, name);
      btn.addEventListener('click', () => this.select(moment, buttons));
      return btn;
    });
    // 矢印キーで選び直せるように（ラジオボタンのお約束）
    row.addEventListener('keydown', (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      event.preventDefault();
      const index = Math.max(0, moments.indexOf(this.selected ?? moments[0]));
      const step = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      const next = (index + step + moments.length) % moments.length;
      this.select(moments[next], buttons);
      buttons[next].focus();
    });
    row.append(...buttons);
    this.element.append(caption, row);
    if (this.selected) this.select(this.selected, buttons);
  }

  private select(moment: Moment, buttons: HTMLButtonElement[]): void {
    this.selected = moment;
    this.moments.forEach((m, i) => {
      const on = m === moment;
      buttons[i].setAttribute('aria-checked', String(on));
      buttons[i].tabIndex = on ? 0 : -1;
    });
    this.onSelect(moment);
  }
}
